// --- FILE: src/contexts/AuthContext.jsx (VERSIÓN FINAL Y ROBUSTA) ---
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db } from '../services/firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { useIdleTimer } from '../hooks/useIdleTimer';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Unificamos adminUser a solo 'user'
  const [activeEmployee, setActiveEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState(null);
  
  // ✅ MEJORA: Usamos sessionStorage para recordar la vista del admin entre recargas
  const [adminView, setAdminView] = useState(() => sessionStorage.getItem('adminView') || 'reports');

  const handleAutoLogout = useCallback(() => {
    if (activeEmployee) {
      setActiveEmployee(null);
      setError('');
    }
  }, [activeEmployee]);

  const idleTimer = useIdleTimer({
    timeout: 10000,
    onIdle: handleAutoLogout,
    enabled: !!activeEmployee
  });

  const login = async (email, password) => { // Unificamos a una sola función de login
    setLoading(true);
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError('Credenciales incorrectas');
      setLoading(false);
      throw err;
    }
  };

  const employeeLogin = async (pin) => {
    setError('');
    try {
      const employeesRef = collection(db, 'sellers');
      const q = query(employeesRef, where('pin', '==', pin), limit(1));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const employeeDoc = querySnapshot.docs[0];
        setActiveEmployee({ id: employeeDoc.id, ...employeeDoc.data() });
        return true;
      } else {
        setError('PIN no válido');
        return false;
      }
    } catch (err) {
      setError("Error al verificar el PIN.");
      return false;
    }
  };

  const logout = async () => { // Unificamos a una sola función de logout
    await signOut(auth);
    sessionStorage.removeItem('adminView'); // Limpiar al cerrar sesión
  };

  const employeeLogout = () => {
    setActiveEmployee(null);
    setError('');
    setAdminView('reports');
    sessionStorage.setItem('adminView', 'reports');
  };

  const navigateToTerminal = () => {
    setAdminView('terminal');
    sessionStorage.setItem('adminView', 'terminal'); // Guardar la preferencia
  };
  
  const navigateToReports = () => {
    setAdminView('reports');
    sessionStorage.setItem('adminView', 'reports'); // Guardar la preferencia
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);
      if (currentUser) {
        setUser(currentUser);
        const idTokenResult = await currentUser.getIdTokenResult(true);
        setUserRole(idTokenResult.claims.role || 'employee');
      } else {
        setUser(null);
        setActiveEmployee(null);
        setUserRole(null);
        setAdminView('reports');
        sessionStorage.removeItem('adminView');
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);
  
  useEffect(() => {
    if (activeEmployee) idleTimer.reset();
    else idleTimer.pause();
  }, [activeEmployee, idleTimer]);

  const value = {
    user, // Unificado
    activeEmployee,
    loading,
    error,
    userRole,
    login, // Unificado
    employeeLogin,
    logout, // Unificado
    employeeLogout,
    adminView,
    navigateToTerminal,
    navigateToReports,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
